import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { SortingState } from "@tanstack/react-table";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import { DynamicMenuItem } from "components/Menuitem/DynamicMenuItem";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { fetchPendingSalesOrderReturn } from "slices/Warehouse/ItemInward2/PendingSalesOrderReturn/thunk";
type PendingSalesOrderReturnProps = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const PendingSalesOrderReturn: React.FC<PendingSalesOrderReturnProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "Pending Sales Order Return";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const selectPendingSalesOrderReturn = createSelector(
    (state: any) => state.PendingSalesOrderReturnReducer || {},
    (item) => ({
      list: item.list || [],
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );
  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(selectPendingSalesOrderReturn);

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate, canCreate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward
  );


  useEffect(() => {
    if (warehouseIdSearch) {
      dispatch(
        fetchPendingSalesOrderReturn({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : "",
          search,
          filters: filterValue,
          warehouse_id: warehouseIdSearch,
        })
      );
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseIdSearch,
  ]);
  //*//

  //*// Last Column Action Button
  const menuItems: any[] = useMemo(() => {
    const items: any[] = [
      {
        icon: AddCircleOutlineIcon,
        label: "Inward",
        permission: canCreate,
        onClick: (row: any) =>
          navigate(
            `/warehouse-sales-return-item-inward-grn/${row?.dispatch_id}`
          ),

        color: "info",
      },
    ];
    return items;
  }, [canCreate, navigate]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canCreate;

    if (!hasPermission) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canUpdate, canCreate, menuItems]);
  //*// Listing tanstack table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return pageIndex * pageSize + rowIndex + 1;
        },
      },
      {
        header: "Dispatch ID",
        accessorKey: "dispatch_number",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        //*// need to change url
        filterFetchUrl: `${api?.warehouse?.item_inward?.sales_order_return_filter_dropdown}/dispatch_number?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.dispatch_number || "-";
        },
      },

      {
        header: "Order No.",
        accessorKey: "order_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.warehouse?.item_inward?.sales_order_return_filter_dropdown}/order_no?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.order_no || "-";
        },
      },
      // {
      //   header: "Quotation No",
      //   accessorKey: "quotation_number",
      //   enableSorting: true,
      //   enableColumnFilter: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: `${api?.warehouse?.item_inward?.sales_order_return_filter_dropdown}/dispatch_number?warehouse_id=${warehouseIdSearch}`,
      //   cell: ({ row }: { row: any }) => {
      //     return row.original.quotation_number || "-";
      //   },
      // },

      {
        header: "Dispatch Date",
        accessorKey: "dispatch_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.dispatch_date
            ? new Date(row.original.dispatch_date).toISOString().split("T")[0]
            : "N/A";
          return `${date}`;
        },
      },

      {
        header: "Issued From",
        accessorKey: "issued_from_warehouse_name",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api?.warehouse?.item_inward?.sales_order_return_filter_dropdown}/issued_from_warehouse_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original?.issued_from_warehouse?.warehouse_name || "-";
        },
      },

      {
        header: "Created Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${date}`;
        },
      },
      ...DynamicAction,
    ],
    [dispatch, list, canUpdate, canView, warehouseIdSearch]
  );

  return (
    <Row id="invoiceList">
      <Col lg={12}>
        <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={list}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default PendingSalesOrderReturn;
